// src/api/notifications.api.ts
import { API } from '.'

/**
 * Backend DTO (guess-friendly)
 * Adjust fields once you have the real response.
 */
export type NotificationDTO = {
  id: string | number
  title?: string
  message?: string
  description?: string
  createdAt?: string // ISO
  date?: string // ISO (if backend uses "date")
  read?: boolean
  readAt?: string | null
  attachmentsCount?: number
  attachments?: Array<any>
}

export type GetNotificationsResponse =
  | NotificationDTO[]
  | { items: NotificationDTO[] }
  | { data: NotificationDTO[] }
  | { notifications: NotificationDTO[] }

function normalizeNotifications(res: any): NotificationDTO[] {
  if (Array.isArray(res)) return res

  if (Array.isArray(res.content)) return res.content // ✅ REQUIRED (Spring)
  if (Array.isArray(res.items)) return res.items
  if (Array.isArray(res.data)) return res.data
  if (Array.isArray(res.notifications)) return res.notifications

  return []
}

/**
 * DELETE /api/v1/notifications/{id}
 */
export async function deleteNotification(id: number): Promise<void> {
  return API<void>(`/notifications/${id}`, {
    method: 'DELETE',
  })
}

/**
 * POST /api/v1/notifications/mark-all-read
 */
export const markAllNotificationsAsRead = () =>
  API<void>('/notifications/mark-all-read', {
    method: 'POST',
  })

/**
 * POST /api/v1/notifications/{id}/read
 */
export async function markNotificationAsRead(id: number | string): Promise<void> {
  await API<void>(
    `/notifications/${id}/read`,
    {
      method: 'POST',
    },
    'Fehler beim Markieren der Benachrichtigung als gelesen',
    'NOTIFICATION_MARK_READ_ERROR'
  )
}

/**
 * GET /api/v1/notifications
 * Optional query params supported if your backend later adds them.
 */
export async function getNotifications(params?: {
  status?: 'all' | 'read' | 'unread'
  page?: number
  pageSize?: number
}) {
  const query = new URLSearchParams()

  if (params?.status && params.status !== 'all') query.set('status', params.status)
  if (params?.page !== undefined) query.set('page', String(params.page))
  if (params?.pageSize !== undefined) query.set('pageSize', String(params.pageSize))

  const suffix = query.toString()
  const res = await API<GetNotificationsResponse>(
    `/notifications${suffix ? `?${suffix}` : ''}`,
    {},
    'Fehler beim Laden der Benachrichtigungen',
    'NOTIFICATIONS_FETCH_ERROR'
  )

  return normalizeNotifications(res)
}
